/**
 * The ⓘ beside a page title, and the panel it opens: that page's how-to video and FAQ
 * (Akhil, 2026-09-30). Content comes from `lib/pageHelp.ts`; this file only presents it.
 *
 * The FAQ is a list of native `<details>` — keyboard- and screen-reader-accessible for free, and
 * several can be open at once, which is what someone comparing two answers wants. The video slot
 * says "coming soon" until a link is recorded rather than showing an empty player.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from './Modal'
import { helpFor, videoEmbed, videoSource, type PageHelp } from '../lib/pageHelp'
import { useFileObjectUrl } from '../lib/api/fileDownload'
import { fileHref } from '../lib/api/fileUrl'

export function PageHelpButton({ page }: { page: string }) {
  const [open, setOpen] = useState(false)
  const help = helpFor(page)
  if (!help) return null
  return (
    <>
      <button
        type="button"
        className="helpbtn"
        aria-label={`How to use ${help.title} — video and FAQ`}
        title={`How to use ${help.title}`}
        onClick={() => setOpen(true)}
      >
        i
      </button>
      {/* Portalled to <body>: the button sits INSIDE the page's <h1>, and a dialog rendered there
          inherits the heading's type size and is read by a screen reader as part of the title. */}
      {open ? createPortal(<PageHelpPanel help={help} onClose={() => setOpen(false)} />, document.body) : null}
    </>
  )
}

function PageHelpPanel({ help, onClose }: { help: PageHelp; onClose: () => void }) {
  const video = videoEmbed(help.video)
  return (
    <Modal title={`${help.title} — how it works`} onClose={onClose} width={760}>
      <div className="pad" style={{ display: 'grid', gap: 16 }}>
        <p style={{ margin: 0, color: 'var(--muted)' }}>{help.summary}</p>

        <section aria-label="How-to video">
          {video === null ? (
            <div className="helpvideo empty">
              <div style={{ fontSize: 22, marginBottom: 4 }} aria-hidden>
                ▶
              </div>
              <b>Video coming soon</b>
              <div style={{ fontSize: 12.5, marginTop: 2 }}>A short walkthrough of this page will appear here.</div>
            </div>
          ) : video.kind === 'iframe' ? (
            <div className="helpvideo">
              <iframe
                src={video.src}
                title={`${help.title} walkthrough`}
                allow="fullscreen; picture-in-picture"
                allowFullScreen
                // No referrer to the video host — the page URL can carry an enquiry id.
                referrerPolicy="no-referrer"
                loading="lazy"
              />
            </div>
          ) : video.kind === 'file' ? (
            <HelpVideoFile path={video.src} title={help.title} />
          ) : (
            <a className="linkish" href={video.src} target="_blank" rel="noopener noreferrer">
              ▶ Watch the walkthrough for {help.title}
            </a>
          )}
        </section>

        <section aria-label="Frequently asked questions">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Frequently asked</div>
          <div className="helpfaq">
            {help.faqs.map((faq) => (
              <details key={faq.q}>
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
        </section>
      </div>
    </Modal>
  )
}

/**
 * A direct video file in the browser's own player — see `videoSource` for where it loads from.
 *
 * A Frappe-uploaded file is fetched with credentials into a blob first (`useFileObjectUrl`, the
 * path attachment previews use), because a bare `<video src>` sends none and gets a 401 on the
 * deployed app. Keep those short: the whole file downloads before it plays. A file shipped with
 * the app (`public/help/`) streams directly and can be any length.
 */
function HelpVideoFile({ path, title }: { path: string; title: string }) {
  const source = videoSource(path, import.meta.env.BASE_URL)
  const authorised = useFileObjectUrl(source.from === 'frappe' ? fileHref(source.src) : null)
  const [failed, setFailed] = useState(false)
  const src = source.from === 'frappe' ? authorised.src : source.src
  if (failed || authorised.error) {
    return (
      <div className="helpvideo empty">
        <b>The video could not be played</b>
        <div style={{ fontSize: 12.5, marginTop: 2 }}>{authorised.error ?? 'The file is missing or in a format this browser cannot play (use H.264 MP4).'}</div>
      </div>
    )
  }
  if (!src) {
    return (
      <div className="helpvideo empty">
        <div style={{ fontSize: 12.5 }}>Loading the video…</div>
      </div>
    )
  }
  return (
    <div className="helpvideo">
      <video src={src} controls playsInline preload="metadata" aria-label={`${title} walkthrough`} onError={() => setFailed(true)} />
    </div>
  )
}
